import { useCallback, useState } from 'react';
import Link from 'next/link';
import { useSignIn } from '@clerk/nextjs';
import { isClerkAPIResponseError, isClerkError } from '@clerk/shared/error';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';

import { prepareSecondFactor } from '../factors';
import type { SignInStrategy } from '../type';

const schema = z.object({
  password: z.string().trim(),
});

type Props = Readonly<{
  onChangeStrategy: (strategy?: SignInStrategy) => void;
  onSubmit: () => void;
}>;

export const PasswordVerification = ({ onChangeStrategy, onSubmit }: Props) => {
  const { signIn, fetchStatus } = useSignIn();

  const [passwordInputType, setPasswordInputType] = useState<'password' | 'text'>('password');

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
    },
  });

  const handleSubmit = useCallback(
    async ({ password }: z.infer<typeof schema>) => {
      const { error } = await signIn.password({
        password,
      });
      if (error) {
        if (isClerkAPIResponseError(error)) {
          form.setError('password', { type: 'manual', message: error.errors[0].longMessage });
        } else if (isClerkError(error)) {
          form.setError('password', { type: 'manual', message: error.longMessage });
        }
        console.error(JSON.stringify(error, null, 2));
        return;
      }

      if (signIn.status === 'complete') {
        onSubmit();
      } else if (signIn.status === 'needs_second_factor') {
        // See https://clerk.com/docs/guides/development/custom-flows/authentication/multi-factor-authentication
      } else if (signIn.status === 'needs_client_trust') {
        await prepareSecondFactor({ strategy: 'email_code', signIn });
        onChangeStrategy('email_code');
      } else {
        // If the status is not complete, check why. User may need to
        // complete further steps.
        console.error(JSON.stringify(signIn, null, 2));
      }
    },
    [form, onSubmit, signIn, onChangeStrategy],
  );

  return (
    <Card className="w-full min-w-96">
      <CardHeader>
        <CardTitle>Enter your password</CardTitle>
        <CardDescription>Welcome back {signIn?.identifier}</CardDescription>
      </CardHeader>
      <CardContent>
        <form id="password-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <FieldGroup>
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                  <InputGroup>
                    <InputGroupInput
                      {...field}
                      id={field.name}
                      type={passwordInputType}
                      aria-invalid={fieldState.invalid}
                      autoComplete="current-password"
                      autoCapitalize="none"
                      spellCheck="false"
                      autoFocus
                    />
                    <InputGroupAddon align="inline-end">
                      <InputGroupButton
                        aria-label="Show"
                        title="Show"
                        size="icon-sm"
                        onClick={() => {
                          setPasswordInputType((prev) => (prev === 'password' ? 'text' : 'password'));
                        }}
                      >
                        {passwordInputType === 'password' ? <EyeIcon /> : <EyeOffIcon />}
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                  <FieldDescription className="text-end">
                    <Link
                      href="/forgot-password"
                      className={buttonVariants({ variant: 'link', size: 'sm', className: 'h-auto text-xs' })}
                      onClick={() => signIn.reset()}
                    >
                      Forgot Password?
                    </Link>
                  </FieldDescription>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter>
        <Field orientation="vertical">
          <Button type="submit" form="password-form" className="w-full" disabled={fetchStatus === 'fetching'}>
            Continue
          </Button>
          <Button type="button" size="sm" variant="link" onClick={() => onChangeStrategy()}>
            Use another method
          </Button>
        </Field>
      </CardFooter>
    </Card>
  );
};
